import React, { useEffect, useState, useContext } from "react";
import {
  IAddUpdateAccessControlPageProps,
  IAddUpdateAccessControlPageState,
  IRolesResponseObject,
  IClientsResponseObject,
  IMultiSelectOption,
  IFilterPayload,
} from "../../../interfaces/Index";
import FormSectionLayout from "../../layouts/FormSectionLayout";
import TextField from "../../common/TextField/TextField";
import ButtonComponent from "../../common/Button/ButtonComponent";
import backArrowIcon from "../../../assets/images/back_arrow.svg";
import { Container, Row, Col } from "reactstrap";
import { Form } from "reactstrap";
import "./AddUpdateAccessControlPage.scss";
import {
  phoneNumberValidate,
  alphaNumericValidator,
  phoneNumberOnlyValidator,
  emailAddressValidator,
  cnicNumberValidate,
  emailValidate,
  cnicNumberOnlyValidator,
  clientAndFilterValidator,
} from "../../../utils/Validators";
import Toaster from "../../common/Toaster/Toaster";
import LoaderComponent from "../../common/Loader/Loader";
import HeadingText from "../../common/HeadingText/HeadingText";
import { numbersOnly, clientsRoleValidator } from "../../../utils/Validators";
import {
  normalizeAccessControlUserCreatePayload,
  normalizeAccessControlUserUpdatePayload,
} from "../../../normalizer/AccessControlNormalizer";
import MultiSelectDropDown from "../../common/MultiSelectDropDown/MultiSelectDropDown";
import DropdownButton from "../../common/ButtonDropDown/ButtonDropDown";
import { AbilityContext } from "../../../config/Can";
import { access, organizationIds } from "../../../config/constants";
import { autoGeneratePassword } from "../../../utils/lib";
import { updateUserPasswordApiCall } from "redux/sources/CustomerSource";
import Modal from "components/common/Modal/Modal";
import SearchableSelect from "components/common/SearchableSelect/SearchableSelect";
import { AccountStatus } from "redux/sources/UserSource";

export const copyPassword = (e: any, setShowPassword: any) => {
  if (e) {
    navigator.clipboard.writeText(e);
    setShowPassword(true);
  }
};

export const autoGeneratePass = autoGeneratePassword();

export const getFiltersFromList = (
  options: Array<IFilterPayload>,
  selectedVal: Array<IFilterPayload>
): any => {
  return options.filter((item) =>
    selectedVal.some((val) => val.id === item.id)
  );
};

const AddUpdateAccessControlPage: React.FunctionComponent<
  IAddUpdateAccessControlPageProps
> = (props: IAddUpdateAccessControlPageProps) => {
  const ability = useContext(AbilityContext);
  const [accessControlState, setAccessControlState] =
    useState<IAddUpdateAccessControlPageState>({
      id: props.accessControlUser ? props.accessControlUser.id || "" : "",
      fullName: props.accessControlUser
        ? props.accessControlUser.fullName || ""
        : "",
      phoneNumber: props.accessControlUser
        ? props.accessControlUser.phoneNumber || ""
        : "",
      email: props.accessControlUser ? props.accessControlUser.email || "" : "",
      nationalId: props.accessControlUser
        ? props.accessControlUser.nationalId || ""
        : "",
      password: props.accessControlUser
        ? props.accessControlUser.password || ""
        : autoGeneratePass,

      clients: props.accessControlUser ? props.accessControlUser.clients : [],
      roles: props.accessControlUser ? props.accessControlUser.roles : [],
      status: props.accessControlUser
        ? props.accessControlUser.status === "ENABLED"
          ? true
          : false
        : false,
      resetPasswordModal: false,
      filters: props.accessControlUser ? props.accessControlUser.filters : [],
      organizationId: organizationIds.filter(
        (el) => el.id === props.accessControlUser?.organizationId
      ),
    });

  const [showToaster, setToaster] = useState({
    isVisible: false,
    message: "",
    isError: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [clients, setClients] = useState<IClientsResponseObject[]>(
    props.testClients || []
  );
  const [clientRoles, setClientRoles] = useState<IRolesResponseObject[]>(
    props.testClientRoles || []
  );

  const [filterDropdown, setFilterDropdown] = useState<IMultiSelectOption[]>(
    []
  );

  useEffect(() => {
    const { getAllRolesAndClients, fetchAllFilters } = props;
    getAllRolesAndClients();
    fetchAllFilters();
  }, []);

  useEffect(() => {
    const {
      isAccessControlUserAdded,
      isAccessControlUserUpdated,
      backButtonHandler,
      clearAccessControlUserLevelDetails,
    } = props;
    if (isAccessControlUserAdded || isAccessControlUserUpdated) {
      clearAccessControlUserLevelDetails();
      backButtonHandler(true);
    }
  }, [props.isAccessControlUserAdded, props.isAccessControlUserUpdated]);

  useEffect(() => {
    const { isAllClientsRolesFetched, allClientsRoles } = props;
    if (isAllClientsRolesFetched) {
      let clients: any = [];
      let roles: any = [];
      allClientsRoles?.forEach((clientRoles) => {
        clients.push({ id: clientRoles.id, name: clientRoles.name });
        roles.push(...(clientRoles.roles as Array<IRolesResponseObject>));
      });

      setClients(clients);
      setClientRoles(roles);
    }
  }, [props.isAllClientsRolesFetched, props.allClientsRoles]);

  useEffect(() => {
    const { filters, isAllFiltersFetched } = props;
    if (isAllFiltersFetched && filters.length > 0) {
      setFilterDropdown(props.filters);
    }
    if (isEdit && accessControlUser && isAllFiltersFetched) {
      accessControlState.filters = getFiltersFromList(
        filters,
        accessControlUser.filters
      );
      setAccessControlState({ ...accessControlState });
    }
  }, [props.isAllFiltersFetched]);

  const handleToaster = (error: boolean, message: string) => {
    setToaster({
      isVisible: true,
      message: message,
      isError: error,
    });
  };

  useEffect(() => {
    const { error, isError } = props;
    if (isError) {
      alert((error! as any).errors);
      handleToaster(true, (error! as any).errors);
    }
  }, [props.isError]);

  const handleSubmit = (e: any) => {
    const validationResult = clientsRoleValidator(
      accessControlState.roles,
      accessControlState.clients
    );

    const filterValidationResult = clientAndFilterValidator(
      accessControlState.filters,
      accessControlState.clients
    );

    if (validationResult.isError)
      return alert(validationResult.errors.join("\n"));

    if (filterValidationResult.isError)
      return alert(filterValidationResult.errors.join("\n"));

    const { createUserClientAndRoles, isEdit, updateUserClientAndRoles } =
      props;
    if (isEdit) {
      const reqPayload =
        normalizeAccessControlUserUpdatePayload(accessControlState);
      updateUserClientAndRoles(reqPayload);
    } else {
      const reqPayload =
        normalizeAccessControlUserCreatePayload(accessControlState);
      createUserClientAndRoles(reqPayload);
    }
  };

  const handleChange = (e: any) => {
    const fieldId: string = e.target.id;
    const fieldValue: string = e.target.value;
    setAccessControlState({
      ...accessControlState,
      [fieldId]: fieldValue,
    });
  };

  const handleSearchableSelect = (e: any, id: string) => {
    setAccessControlState({
      ...accessControlState,
      [id]: e,
    });
  };

  const handleClientChange = (e: any) => {
    const clients = accessControlState;
    clients.clients = e.slice();
    setAccessControlState({
      ...clients,
    });
  };

  const resetPassword = () => {
    let { accessControlUser, isEdit, backButtonHandler } = props;
    let userId: any = accessControlUser && accessControlUser.id;
    let passwordPayload = {
      password: autoGeneratePass,
      passwordConfirmation: autoGeneratePass,
    };
    if (isEdit && (updateUserPasswordApiCall(userId, passwordPayload) as any)) {
      resetPasswordConfirmationModal();
      backButtonHandler(true, "passwordResetSuccess");
    } else {
      backButtonHandler(false, "passwordResetUnsuccessful");
    }
  };

  const resetPasswordConfirmationModal = () => {
    setAccessControlState({
      ...accessControlState,
      resetPasswordModal: !accessControlState.resetPasswordModal,
    });
  };

  const handleRolesChange = (e: any) => {
    const roles = accessControlState;
    roles.roles = e.slice();
    setAccessControlState({
      ...roles,
    });
  };

  const handleFilterChange = (selectedFilters: IFilterPayload[]) => {
    setAccessControlState({
      ...accessControlState,
      filters: selectedFilters,
    });
  };

  const handleCnic = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (numbersOnly(e.target.value)) {
      if (
        e.target.value.length > accessControlState.nationalId.length ||
        Math.abs(e.target.value.length - accessControlState.nationalId.length) >
          1
      ) {
        const formattedCnic = formatnationalId(
          e.target.value.replace(/-/g, "")
        );
        accessControlState.nationalId = formattedCnic;
        setAccessControlState({ ...accessControlState });
      } else {
        accessControlState.nationalId = e.target.value;
        setAccessControlState({ ...accessControlState });
      }
    }
  };

  const formatnationalId = (str: string) => {
    if (str.length >= 5) {
      if (str.length >= 12) {
        let firstPart = str.substring(0, 5);
        let secondPart = str.substring(5, str.length);
        firstPart += "-";
        let newFinal = firstPart + secondPart;
        firstPart = newFinal.substring(0, 13);
        firstPart += "-";
        secondPart = newFinal.substring(13, newFinal.length);
        return firstPart + secondPart;
      } else {
        let firstPart = str.substring(0, 5);
        let secondPart = str.substring(5, str.length);
        firstPart += "-";
        return firstPart + secondPart;
      }
    }
    return str;
  };

  useEffect(() => {
    const { clearAccessControlUserLevelDetails } = props;
    return () => {
      clearAccessControlUserLevelDetails();
    };
  }, []);

  const handleGoBackButton = () => {
    const { backButtonHandler, clearAccessControlUserLevelDetails } = props;
    clearAccessControlUserLevelDetails();
    backButtonHandler(false);
  };

  const {
    fullName,
    phoneNumber,
    password,
    nationalId,
    resetPasswordModal,
    email,
    organizationId,
    status,
  } = accessControlState;
  const validateForm =
    fullName.length > 0 &&
    phoneNumber.length === 11 &&
    (nationalId.length === 0 || nationalId.length === 15) &&
    !emailAddressValidator(email);

  const { isEdit, isLoading, accessControlUser } = props;

  const isPasswordDisabled = isEdit ? false : true;

  const buttonText = isEdit ? "Update details" : "Create a user";

  const headingText = isEdit
    ? (accessControlUser?.fullName as string)
    : "New account";

  const isPhoneNumberDisabled = isEdit ? true : false;

  const handleDeactivateAccount = (
    selectedValue: AccountStatus.DISABLED | AccountStatus.ENABLED
  ) => {
    setAccessControlState({
      ...accessControlState,
      status: selectedValue === AccountStatus.ENABLED,
    });
  };

  const renderUserAccessSection = () => {
    const { accessControlUser, userInfo } = props;

    let allowAccess = false;
    if (userInfo.id === accessControlUser?.id) {
      allowAccess = ability.can(
        "read",
        new access({ userId: accessControlUser?.id })
      );
    } else {
      allowAccess = ability.can("read", "access");
    }

    const updateAccess = ability.can("update", "access");
    const createAccess = ability.can("create", "access");

    if (allowAccess && (updateAccess || createAccess)) {
      return (
        <FormSectionLayout subTitleText="User access">
          <MultiSelectDropDown
            options={clients}
            label="Client"
            selectedValues={accessControlState.clients}
            placeholder="Please select platforms which apply"
            displayValue="name"
            onChange={handleClientChange}
            onRemove={handleClientChange}
          />
          <MultiSelectDropDown
            options={clientRoles}
            label="Assigned Role"
            selectedValues={accessControlState.roles}
            placeholder="Search by team name or domain"
            displayValue="title"
            onChange={handleRolesChange}
            onRemove={handleRolesChange}
          />
          <MultiSelectDropDown
            options={filterDropdown}
            label="Filters"
            selectedValues={accessControlState.filters}
            placeholder="Search by filter name"
            displayValue="title"
            onChange={handleFilterChange}
            onRemove={handleFilterChange}
          />
        </FormSectionLayout>
      );
    }
  };

  const AccountStatusOptions = [
    {
      id: AccountStatus.ENABLED,
      label: "Enable",
    },
    {
      id: AccountStatus.DISABLED,
      label: "Disable",
    },
  ];

  return (
    <div>
      <Modal
        showModal={resetPasswordModal}
        closeModalHandler={resetPasswordConfirmationModal}
        modalHeading="Change password"
        showProgressString={false}
        failedRecords=""
        modalBodyClass="verify-account"
      >
        {
          <>
            <p>Are you sure you want to change the password?</p>
            <Row>
              <Col className="col-lg-5">
                <ButtonComponent
                  buttonClass="button-full btn btn-primary-outline"
                  buttonText="Go back"
                  onClickHandler={resetPasswordConfirmationModal}
                />
              </Col>
              <Col className="col-lg-7 no-padding-left">
                <ButtonComponent
                  buttonClass="button-full btn btn-primary"
                  buttonText="Change Password"
                  onClickHandler={resetPassword}
                />
              </Col>
            </Row>
          </>
        }
      </Modal>
      {isLoading && <LoaderComponent />}
      <Toaster
        showToaster={showToaster.isVisible}
        toasterClass={
          showToaster.isError
            ? "toaster toaster-bg-danger"
            : "toaster toaster-bg-success"
        }
      >
        {showToaster.message || ""}
      </Toaster>

      <Toaster
        showToaster={showPassword}
        toasterClass="toaster toaster-bg-success"
      >
        Password Copied
      </Toaster>

      <Container fluid>
        <Row>
          <Col>
            <div className="back-button">
              <a href="javascript:void(0)" onClick={handleGoBackButton}>
                <span>
                  <img src={backArrowIcon} alt="close" />
                </span>{" "}
                Back to Users
              </a>
            </div>
            <HeadingText text={headingText} isMainHeading={true} />
          </Col>
        </Row>
        <Row className="form-container">
          <Col xs="12">
            <Form name="access-control-add-update-form">
              <div className="account-detail">
                <FormSectionLayout subTitleText="Account details">
                  <TextField
                    id="fullName"
                    label="Full Name"
                    placeholder="e.g. Bill Gates"
                    value={fullName}
                    handleChange={handleChange}
                    error={alphaNumericValidator(fullName)}
                  />
                  <TextField
                    id="phoneNumber"
                    label="Phone Number"
                    placeholder="e.g. 03421231231"
                    value={phoneNumber}
                    handleChange={handleChange}
                    validator={phoneNumberValidate}
                    error={phoneNumberOnlyValidator(phoneNumber)}
                    disabled={isPhoneNumberDisabled}
                  />
                  <TextField
                    id="nationalId"
                    label="CNIC Number"
                    placeholder="e.g. 11111-1111111-1"
                    value={nationalId}
                    handleChange={handleCnic}
                    validator={cnicNumberValidate}
                    maxLength={15}
                    error={cnicNumberOnlyValidator(nationalId)}
                    isOptional={true}
                  />
                  <TextField
                    id="email"
                    label="Email"
                    placeholder="e.g. abc.xyz@bazaar-app.com"
                    value={email}
                    handleChange={handleChange}
                    validator={emailValidate}
                    maxLength={50}
                    error={emailAddressValidator(email)}
                    isOptional={true}
                  />
                  <div className={isEdit ? "form-row" : ""}>
                    <Col>
                      {isEdit && ability.can("update", "password") && (
                        <ButtonComponent
                          buttonClass="btn btn-primary-outline button-large reset-button"
                          buttonText="Reset password"
                          onClickHandler={resetPasswordConfirmationModal}
                        />
                      )}
                    </Col>
                  </div>
                </FormSectionLayout>
                <div id="clientRoles">{renderUserAccessSection()}</div>
                {isEdit && (
                  <FormSectionLayout subTitleText="Organizations">
                    <SearchableSelect
                      id="organizationId"
                      isSearchable={true}
                      options={organizationIds}
                      onChange={(e: any) => {
                        handleSearchableSelect(e, "organizationId");
                      }}
                      label="Organization"
                      placeholder="Select organization Id"
                      selectedValue={organizationId}
                    />
                  </FormSectionLayout>
                )}
                {isEdit && ability.can("update", "user") && (
                  // <Col lg="5">
                  //   <DropdownButton
                  //     classNames="dropdown more-actions deactivate-account"
                  //     dropDownButtonText="More actions"
                  //     dropdownItem={customersMoreActions}
                  //   />
                  // </Col>
                  <FormSectionLayout subTitleText="Account Status">
                    <SearchableSelect
                      id="status"
                      isSearchable={true}
                      options={AccountStatusOptions}
                      onChange={(e: any) => {
                        handleDeactivateAccount(e.id);
                      }}
                      label="Account Status"
                      placeholder="Select account status"
                      selectedValue={
                        status
                          ? AccountStatusOptions[0]
                          : AccountStatusOptions[1]
                      }
                    />
                  </FormSectionLayout>
                )}
              </div>
              <div className="form-row-separator">
                <FormSectionLayout subTitleText="">
                  <div className="form-row-separator">
                    <Row id="addEditButtonRow">
                      {(ability.can("update", "user") ||
                        ability.can("create", "user")) && (
                        <ButtonComponent
                          buttonClass="large-button btn-primary ml-3"
                          buttonText={buttonText}
                          onClickHandler={handleSubmit}
                          isDisable={props.isLoading || !validateForm}
                        />
                      )}
                    </Row>
                  </div>
                </FormSectionLayout>
              </div>
            </Form>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AddUpdateAccessControlPage;
